import {AfterViewInit, Component, ElementRef, Input, OnInit, Renderer2, ViewChild} from '@angular/core';
import {getHTMLControlName, Wertemenge} from '../../../models/uiArtefakt';
import {FondsService} from '../../../services/fonds.service';
import {Observable, of, switchMap, tap} from 'rxjs';
import {Fondsgruppe} from '../../../models/fondsgruppe';
import {map, take} from 'rxjs/operators';
import {UIAusrichtung} from '../../../models/uiAusrichtung';
import {NgForm} from '@angular/forms';
import {ValidationService} from '../../../services/validation.service';
import {ShellConfigService} from '../../../services/shell-config.service';
import {UiUpdateService} from '../../../services/ui-update.service';
import {TranslationService} from '@lv1871/core';
import {CONSTANTS} from '../../../models/constants/constants';

@Component({
    selector: 'lv-gemanagte-portfolioloesung',
    templateUrl: './gemanagte-portfolioloesung.component.html',
    styleUrls: ['./gemanagte-portfolioloesung.component.scss'],
    standalone: false
})
export class GemanagtePortfolioloesungComponent implements OnInit, AfterViewInit {
  @ViewChild(NgForm, {static: true})
  private _ngForm;

  public controlName: string;
  public managedFonds$: Observable<Fondsgruppe[][]>;
  public fondsNameMap$: Observable<Map<number, string>>;
  public model: Fondsgruppe;
  public radiobuttonValue: string;
  public uiAusrichtung: UIAusrichtung;
  public showUntruncatedError: boolean = false;
  public lastSelectedDropdownModel: Fondsgruppe;

  private _fondsMap: Map<string, Fondsgruppe[]>;
  private _uiArtefakt: Wertemenge;

  constructor(private _shellConfigService: ShellConfigService, private _uiUpdateService: UiUpdateService,
              private _translationService: TranslationService, private _validationService: ValidationService,
              private _fondsService: FondsService,
              private _renderer: Renderer2, private _element: ElementRef) {}

  get uiArtefakt(): Wertemenge {
    return this._uiArtefakt;
  }

  @Input()
  set uiArtefakt(uiArtefakt: Wertemenge) {
    this._uiArtefakt = uiArtefakt;
    this.managedFonds$ = this._fondsService.getFondsgruppenInProdukt(this._uiArtefakt).pipe(
        map(fondsgruppenInProdukt => fondsgruppenInProdukt.fondsgruppeManaged),
        tap(fondsMap => {
          this._fondsMap = fondsMap;
          if (!this.lastSelectedDropdownModel) {
            let defaultGruppe: Fondsgruppe;
            fondsMap.forEach(fondsgruppe => {
              if (!defaultGruppe && fondsgruppe.length > 1) {
                fondsgruppe.sort().reverse();
                fondsgruppe.forEach(element => {
                  if (element.name === CONSTANTS.DEFAULT_AKTIENGEWICHTUNG_ETF_PORTFOLIO_PLUS_NAME) {
                    defaultGruppe = element;
                  }
                });
                if(!defaultGruppe) {
                  defaultGruppe = fondsgruppe[0];
                }
              }
            });
            this.lastSelectedDropdownModel = defaultGruppe;
          }
          this._setModel();
        }),
        map(fondsMap => [...fondsMap.values()]),
        switchMap(alleManagedFonds => {
          let foundFondsgruppe;
          Array.from(this._fondsMap.values()).forEach(fondsgruppe => {
            fondsgruppe.forEach(gruppe => {
              if (gruppe.fondsgruppeId + '' === this._uiArtefakt.WertEingabe + '') {
                foundFondsgruppe = gruppe;
              }
            });
          });
          if (foundFondsgruppe?.fondsgruppeItems?.length) {
            return this._fondsService.setSelectedFondsInUiControl(foundFondsgruppe.fondsgruppeItems,
                this._fondsService.getAnlageKontext(this._uiArtefakt.FeldnameExtern)).pipe(
                map(_ => alleManagedFonds)
            );
          } else {
            return of(alleManagedFonds);
          }
        })
    );
  }

  ngOnInit(): void {
    this.controlName = getHTMLControlName(this.uiArtefakt);
    this.uiAusrichtung = this._shellConfigService.getParameter().uiAusrichtung;
    this._validationService.registerForm(this._uiArtefakt, this._ngForm);
  }

  ngAfterViewInit(): void {
    this._renderer.setAttribute(this._element.nativeElement, 'cypress-uuid', this.uiArtefakt.IdentifikatorUuid);
    if(!!this.uiArtefakt.ReferenzIdentifikator) {
      this._renderer.setAttribute(this._element.nativeElement, 'cypress-refId', this.uiArtefakt.ReferenzIdentifikator);
    }
  }

  changeRadiobuttonSelection($event: Fondsgruppe) {
    this._fondsService.setSelectedFondsInUiControl($event.fondsgruppeItems,
        this._fondsService.getAnlageKontext(this._uiArtefakt.FeldnameExtern)).pipe(
        tap(_ => {
          this.model = $event;
          this._uiArtefakt.WertEingabe = $event.fondsgruppeId;
          this.radiobuttonValue = $event.fondsgruppeId + '';
          this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt);
        }),
        take(1)
    ).subscribe();
  }

  changeDropdown($event: Fondsgruppe) {
    this._fondsService.setSelectedFondsInUiControl($event.fondsgruppeItems,
        this._fondsService.getAnlageKontext(this._uiArtefakt.FeldnameExtern)).pipe(
        tap(_ => {
          this.model = $event;
          this._uiArtefakt.WertEingabe = $event.fondsgruppeId;
          this.radiobuttonValue = $event.portfolio.portfolioCluster.id;
          this._uiUpdateService.valueChangedSubject$.next(this._uiArtefakt);
          this.lastSelectedDropdownModel = this.model;
        }),
        take(1)
    ).subscribe();
  }

  public getError(): string {
    if (this._ngForm.form.status !== 'VALID' && this._ngForm.form?.touched) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this.uiArtefakt.FehlertextPflichtfeld ? this.uiArtefakt.FehlertextPflichtfeld : requiredError;
    }
    this.showUntruncatedError = false;
    return null;
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  private _setModel() {
    if (!this._uiArtefakt?.WertEingabe) {
      return;
    }
    this.radiobuttonValue = this._uiArtefakt?.WertEingabe + '';
    if (!!this.radiobuttonValue && !!this._fondsMap) {
      this._fondsMap.forEach((val, key) => {
        if (val.length !== 1) {
          const foundFondsgruppe = val.filter(fonds => fonds.fondsgruppeId + '' === this.radiobuttonValue + '')[0];
          if (!!foundFondsgruppe) {
            this.model = foundFondsgruppe;
            this.radiobuttonValue = foundFondsgruppe.portfolio.portfolioCluster.id;
            this.lastSelectedDropdownModel = foundFondsgruppe;
          }
        }
      });
    }
  }
}
